import { motion, useIsPresent } from "framer-motion";
import { forwardRef } from "react";
import type { ReactNode } from "react";
import styled from "@emotion/styled";

/**
 * Page-turn transition between phases.
 *
 * Exiting screen settles up & out; entering screen rises into place with a
 * subtle scale settle. Both screens render simultaneously (no AnimatePresence
 * `mode="wait"`) so shared `layoutId` elements can FLIP across phase changes
 * without seams.
 */
const PageWrapper = styled(motion.div)`
    width: 100%;
    display: flex;
    flex-direction: column;
    flex: 1;
    /* Sits between the bounded shell and the screen — without this it takes its
     * content's height as a floor and the screen below can never shrink. */
    min-height: 0;
    transform-origin: 50% 30%;

    &.is-exiting {
        position: absolute;
        inset: 0;
        pointer-events: none;
        z-index: 0;
    }
`;

interface Props {
    children: ReactNode;
    keyId: string;
}

const enterTransition = { duration: 0.55, ease: [0.22, 1, 0.36, 1] as const };
const exitTransition  = { duration: 0.4, ease: [0.55, 0, 0.32, 1] as const };

const PageTransition = forwardRef<HTMLDivElement, Props>(({ children, keyId }, ref) => {
    const isPresent = useIsPresent();
    return (
        <PageWrapper
            ref={ref}
            className={!isPresent ? "is-exiting" : ""}
            key={keyId}
            initial={{ opacity: 0, y: 18, scale: 0.985 }}
            animate={{ opacity: 1, y: 0, scale: 1, transition: enterTransition }}
            exit={{ opacity: 0, y: -10, scale: 0.992, transition: exitTransition }}
        >
            {children}
        </PageWrapper>
    );
});

export default PageTransition;
